import { TrashIcon } from '@heroicons/react/24/outline'
import { Input2, SearchableSelect } from 'stories/components'
import { removeComma, thousandSeperatorWithPrefix } from 'utils'

/**
 * One charge collected on a payoff. Mirrors the backend PayoffChargeLine.
 *
 * A line with an `id` is a charge the loan already owes, which the payoff is collecting. A line
 * without one is a fee the servicer added here (seeded from the tenant's Payoff Defaults) and is
 * booked as a real loan charge when the payment is applied, so it carries a category and pays out
 * through that category's fee splits like any other collected charge.
 */
export interface PayoffChargeLine {
  id?: number | null
  title: string
  amount: number | string
  chargeCategory?: string
}

const emptyLine = (): PayoffChargeLine => ({ id: null, title: '', amount: '', chargeCategory: '' })

/** Total of the lines, coercing the raw amount strings the inputs hold while being edited. */
export const sumPayoffCharges = (rows: PayoffChargeLine[] = []): number =>
  Number(
    (rows || []).reduce((sum, row) => sum + Number(removeComma(row?.amount ?? 0) || 0), 0).toFixed(2),
  )

/**
 * The charges a payoff collects, as an editable list. Shown only for a payoff: every other payment
 * type picks charges through Pay Charges instead.
 *
 * Seeded with the charges the loan already owes plus the fees this lender quotes on every payoff,
 * then left to the servicer: a payoff is negotiated, so the fee that applies to this one is theirs
 * to decide. The total drives the payment's Charges figure, which is why it is spelled out here.
 *
 * An existing charge's description and category are fixed - they belong to a charge that is already
 * on the loan, and editing them here would change nothing - so only its amount can be changed, to
 * collect part of it. A fee the payoff is adding is fully editable until the payment is applied.
 */
export const PayoffChargesEditor = ({
  value,
  onChange,
  categories,
  disabled = false,
  title = 'Payoff Charges',
  hint = 'Charges collected on this payoff. Seeded from the loan’s open charges and your configured payoff fees; the total becomes the Charges figure above.',
  interestThrough = '',
  interestThroughDefault = '',
  onInterestThroughChange,
}: {
  value: PayoffChargeLine[]
  onChange: (rows: PayoffChargeLine[]) => void
  /** Charge category options, keyed by the value stored on the charge. */
  categories: Record<string, string>
  disabled?: boolean
  title?: string
  hint?: string
  /** The date the amounts are quoted through, when the servicer has pinned one. */
  interestThrough?: string
  /** The date they follow when nothing is pinned - the payment's own date. */
  interestThroughDefault?: string
  /** Re-price every line to a date. Omitted leaves the dated control off entirely. */
  onInterestThroughChange?: (date: string) => void
}) => {
  const rows = Array.isArray(value) ? value : []

  const update = (index: number, patch: Partial<PayoffChargeLine>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const addRow = () => onChange([...rows, emptyLine()])
  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index))

  const total = sumPayoffCharges(rows)

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <p className="font-semibold text-sm">{title}</p>
        {!disabled && (
          <span className="cursor-pointer text-shade-blue hover:underline text-sm" onClick={addRow}>
            + Add charge
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>

      {/* A charge carrying a rate is worth a day less of interest for every day earlier it is
          quoted, so a payoff settled on a demand issued good through an earlier date collects
          less than the payment date says. One date re-prices every line; the alternative is
          typing over each of them, which on a loan carrying 22 force-placed insurance charges is
          what the servicer asked not to have to do. */}
      {!disabled && onInterestThroughChange && (
        <div className="flex items-end gap-3 mb-3">
          <div className="w-44">
            <Input2
              title="Interest through"
              type="date"
              value={interestThrough}
              onChange={(v: string) => onInterestThroughChange(v)}
            />
          </div>
          <p className="text-xs text-gray-500 pb-2">
            {interestThrough ? (
              <>
                Amounts are quoted through this date instead of the payment date.{' '}
                <span
                  className="cursor-pointer text-shade-blue hover:underline"
                  onClick={() => onInterestThroughChange('')}
                >
                  Use the payment date
                </span>
              </>
            ) : (
              `Amounts follow the payment date${
                interestThroughDefault ? ` (${interestThroughDefault})` : ''
              }. Set a date to quote them through an earlier day, as a demand good through that date did.`
            )}
          </p>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No charges on this payoff.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row, index) => {
            const booked = Number(row?.id) > 0
            return (
              <div className="grid grid-cols-12 gap-2 items-end" key={row.id ?? `new-${index}`}>
                <div className="col-span-5">
                  <Input2
                    title="Charge"
                    value={String(row.title ?? '')}
                    // A booked charge's description is the charge's own; it is shown so the servicer
                    // can see what is being collected, not so it can be renamed from here.
                    disabled={disabled || booked}
                    error={
                      !String(row.title || '').trim() && Number(removeComma(row.amount ?? 0) || 0) > 0
                        ? 'Name this charge'
                        : ''
                    }
                    onChange={(v: string) => update(index, { title: v })}
                  />
                </div>
                <div className="col-span-3">
                  <SearchableSelect
                    id={`payoff-charge-category-${index}`}
                    title="Category"
                    value={String(row.chargeCategory ?? '')}
                    options={categories}
                    hasDefaultOption
                    defaultOptionText="Uncategorized"
                    // Same reason as the description: the category lives on the booked charge.
                    disabled={disabled || booked}
                    onChange={(v: string) => update(index, { chargeCategory: v })}
                  />
                </div>
                <div className="col-span-3">
                  <Input2
                    title="Amount"
                    type="thousandSep"
                    prefix="$"
                    value={String(row.amount ?? '')}
                    disabled={disabled}
                    onChange={(v: string) => update(index, { amount: v })}
                  />
                </div>
                <div className="col-span-1 flex justify-center pb-2">
                  {!disabled && (
                    <TrashIcon
                      className="w-5 h-5 text-red-500 cursor-pointer"
                      onClick={() => removeRow(index)}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <p className="text-right text-sm font-semibold mt-2">
        Total: {thousandSeperatorWithPrefix(total)}
      </p>
    </div>
  )
}
